import React from "react";
import { Crown, User, AlertTriangle } from "lucide-react";

interface CustomerPlanBadgeProps {
  planType?: string | null;
  subscriptionStatus?: string | null;
  compact?: boolean;
}

type PlanStyle = {
  label: string;
  classes: string;
  icon: "crown" | "user";
};

const getPlanStyle = (planType?: string | null): PlanStyle => {
  if (!planType) {
    return {
      label: "ANONYME",
      classes: "bg-gray-500/15 text-gray-400 border-gray-500/30",
      icon: "user",
    };
  }
  if (planType === "free") {
    return {
      label: "FREE",
      classes: "bg-gray-500/15 text-gray-300 border-gray-500/30",
      icon: "user",
    };
  }
  if (planType === "pro") {
    return {
      label: "PRO",
      classes:
        "bg-purple-500/20 text-purple-300 border-purple-500/40 shadow-sm shadow-purple-500/10",
      icon: "crown",
    };
  }
  if (planType.startsWith("ltd_")) {
    return {
      label: "LTD",
      classes:
        "bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-sm shadow-amber-500/10",
      icon: "crown",
    };
  }
  // solo, starter_*, premium_availability, growth_form
  if (planType === "solo") {
    return {
      label: "SOLO",
      classes: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      icon: "crown",
    };
  }
  if (planType.startsWith("starter_")) {
    return {
      label: "STARTER",
      classes: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      icon: "crown",
    };
  }
  if (planType === "premium_availability") {
    return {
      label: "PREMIUM",
      classes: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      icon: "crown",
    };
  }
  if (planType === "growth_form") {
    return {
      label: "GROWTH",
      classes: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      icon: "crown",
    };
  }
  // Fallback : plan inconnu mais non-free → payant générique
  return {
    label: planType.toUpperCase(),
    classes: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    icon: "crown",
  };
};

export const CustomerPlanBadge: React.FC<CustomerPlanBadgeProps> = ({
  planType,
  subscriptionStatus,
  compact = false,
}) => {
  const style = getPlanStyle(planType);
  const Icon = style.icon === "crown" ? Crown : User;

  const isProblematic =
    subscriptionStatus === "past_due" || subscriptionStatus === "cancelled";

  const sizeClasses = compact
    ? "px-1.5 py-0.5 text-[10px] gap-1"
    : "px-2.5 py-1 text-xs gap-1.5";
  const iconSize = compact ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <span
      className={`inline-flex items-center rounded-full border font-bold uppercase tracking-wider ${sizeClasses} ${style.classes}`}
      title={
        subscriptionStatus
          ? `Plan ${style.label} — ${subscriptionStatus}`
          : `Plan ${style.label}`
      }
    >
      <Icon className={iconSize} />
      {style.label}
      {!compact && isProblematic && (
        <AlertTriangle
          className="w-3 h-3 text-red-400"
          aria-label={subscriptionStatus ?? undefined}
        />
      )}
    </span>
  );
};
